import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const effectiveMax = this.getEffectiveMax();
    const { value } = this.getCurrentValues();

    // Minus stays live at the minimum, because there it removes the line (see updateQuantity).
    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Steps the quantity. A decrease at the minimum removes the line, exactly as the trash button in the
   * same row does.
   *
   * History: until 28 Sep 2026 this floored at the minimum, on an audit finding that one tap at quantity 1
   * deleted a line without warning. The user then asked for the opposite, as WooCommerce behaves and
   * shoppers expect: "there's 1 unit, clicking - should act as the trash bin". Removal goes through the
   * row's own remove button, so it gets the same animation, request and empty-cart state, rather than a
   * second hand-written path posting quantity 0. sections/kaasdok-cart.liquid (the /cart page) does the
   * same, so the two surfaces agree.
   * @param {number} stepMultiplier - Positive to increase, negative to decrease.
   */
  updateQuantity(stepMultiplier) {
    const { min, value } = this.getCurrentValues();

    if (stepMultiplier < 0 && value <= min) {
      const removeButton = this.closest('[data-key]')?.querySelector('button.cart-items__remove');
      if (removeButton instanceof HTMLButtonElement && !removeButton.disabled) removeButton.click();
      return;
    }

    super.updateQuantity(stepMultiplier);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
